// Small inline SVG icons for the menus (our own simple geometric drawings; no third-party icon
// sets). Each uses `currentColor`, so CSS sets the colour (ui-qol §4: #7D7D7D glyphs, white on a
// selected mode tile).

import { svg } from './svg.js';

const BASE = { 'aria-hidden': 'true', focusable: 'false', fill: 'none' } as const;

function icon(viewBox: string, ...children: SVGElement[]): SVGSVGElement {
  return svg('svg', { ...BASE, viewBox, class: 'icon' }, ...children);
}

/** Settings gear: a ring with eight teeth. */
export function gearIcon(): SVGSVGElement {
  const teeth: SVGElement[] = [];
  for (let i = 0; i < 8; i++) {
    teeth.push(
      svg('rect', {
        x: 10.5,
        y: 1.5,
        width: 3,
        height: 5,
        rx: 1,
        fill: 'currentColor',
        transform: `rotate(${i * 45} 12 12)`,
      }),
    );
  }
  return icon(
    '0 0 24 24',
    ...teeth,
    svg('circle', { cx: 12, cy: 12, r: 6.5, stroke: 'currentColor', 'stroke-width': 3 }),
  );
}

/** Source code: angle brackets around a slash. */
export function sourceIcon(): SVGSVGElement {
  return icon(
    '0 0 24 24',
    svg('path', {
      d: 'M8 6 2.5 12 8 18M16 6l5.5 6-5.5 6M13.5 4l-3 16',
      stroke: 'currentColor',
      'stroke-width': 2.4,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }),
  );
}

/** Help: a question mark. */
export function helpIcon(): SVGSVGElement {
  return icon(
    '0 0 24 24',
    svg('path', {
      d: 'M8.5 9a3.5 3.5 0 1 1 5.2 3.06c-1.1.62-1.7 1.3-1.7 2.44V15',
      stroke: 'currentColor',
      'stroke-width': 2.6,
      'stroke-linecap': 'round',
    }),
    svg('circle', { cx: 12, cy: 19, r: 1.6, fill: 'currentColor' }),
  );
}

/** A computer mouse (onboarding hint: "Move the mouse to steer"). */
export function mouseIcon(): SVGSVGElement {
  return icon(
    '0 0 16 24',
    svg('rect', {
      x: 1.5,
      y: 1.5,
      width: 13,
      height: 21,
      rx: 6.5,
      stroke: 'currentColor',
      'stroke-width': 2,
    }),
    svg('path', {
      d: 'M8 5v5',
      stroke: 'currentColor',
      'stroke-width': 2,
      'stroke-linecap': 'round',
    }),
  );
}

/** Mode tile icons (ui-qol §4.2), 48 px, drawn as cells. */
export function modeIcon(mode: string): SVGSVGElement {
  const fill = { fill: 'currentColor' };
  switch (mode) {
    case 'battleroyale':
      // A shrinking ring with one cell inside.
      return icon(
        '0 0 48 48',
        svg('circle', {
          cx: 24,
          cy: 24,
          r: 19,
          stroke: 'currentColor',
          'stroke-width': 3,
          'stroke-dasharray': '6 4',
        }),
        svg('circle', { cx: 24, cy: 24, r: 8, ...fill }),
      );
    case 'teams':
      // Three cells, two of them touching.
      return icon(
        '0 0 48 48',
        svg('circle', { cx: 16, cy: 18, r: 9, ...fill }),
        svg('circle', { cx: 32, cy: 18, r: 9, ...fill }),
        svg('circle', { cx: 24, cy: 34, r: 9, ...fill }),
      );
    case 'experimental':
      // A cell with a spiky virus.
      return icon(
        '0 0 48 48',
        svg('path', {
          d: 'M24 6l3 6 6-3-1 7 7 1-4 5 5 4-6 3 2 6-7-1-1 7-4-5-4 5-1-7-7 1 2-6-6-3 5-4-4-5 7-1-1-7 6 3z',
          ...fill,
        }),
      );
    default:
      // Free-for-all: one big cell chasing a small one.
      return icon(
        '0 0 48 48',
        svg('circle', { cx: 20, cy: 26, r: 14, ...fill }),
        svg('circle', { cx: 39, cy: 13, r: 5, ...fill }),
      );
  }
}
